import type { ReactNode } from 'react'
import {
  Popover as PopoverHeadless,
  PopoverButton as PopoverHeadlessButton,
  PopoverPanel as PopoverHeadlessPanel,
  Transition,
} from '@headlessui/react'
import { twMerge } from 'tailwind-merge'

type Align = 'end' | 'start'
type Placement = 'bottom' | 'left' | 'right' | 'top'

interface PopoverProps {
  children: (open: boolean) => ReactNode
  label: JSX.Element | string
  position?: `${Placement}` | `${Placement} ${Align}`
  className?: string
  popoverClassName?: string
}

const Popover = ({
  children,
  label,
  position,
  className,
  popoverClassName,
}: PopoverProps): ReactNode => (
  <PopoverHeadless className="flex items-center">
    {({ open }) => (
      <>
        <PopoverHeadlessButton
          className={twMerge(
            'relative flex cursor-pointer items-center truncate',
            className ?? 'size-full max-w-fit',
            `rounded-full ring-brand-primary ring-offset-1 outline-hidden focus-visible:ring-2`,
          )}
        >
          {children(open)}
        </PopoverHeadlessButton>
        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition duration-75 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <PopoverHeadlessPanel
            portal
            anchor={{ to: position || 'top', gap: 16 }}
            className={twMerge('z-50 rounded-lg shadow-2xl', popoverClassName)}
          >
            <div className="relative w-fit rounded-lg bg-background-secondary px-5 py-3 whitespace-normal">
              <div className="w-full text-center font-avertaPE text-sm font-medium break-normal text-text-tertiary">
                {label}
              </div>
            </div>
          </PopoverHeadlessPanel>
        </Transition>
      </>
    )}
  </PopoverHeadless>
)

export default Popover
